import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'

import type {ClientSource, ConnectorConnectionResponse, Plugin, PluginsResponse} from '../types/connectors'

/**
 * Calls the connectors proxy in mission control.
 *
 * Requires `copilot_mission_control_use_api_gateway`. That flag picks both the
 * host and the token, so with it off these calls reach CAPI carrying a token
 * CAPI cannot read.
 */

/** The gateway forwards only `/cmc_internal/api/agents` to mission control. */
const CONNECTORS_PATH = '/agents/connectors'

/** Selects the consent experience the connectors service renders. */
const CLIENT_SOURCE: ClientSource = 'GITHUB_DOTCOM'

const JSON_HEADERS = {Accept: 'application/json'}

function connectionPath(connector: string): string {
  return `${CONNECTORS_PATH}/${encodeURIComponent(connector)}/connection`
}

/** Catalog annotated with the caller's connection status. */
export async function fetchPlugins(signal?: AbortSignal): Promise<Plugin[]> {
  const response = await fetchCAPI({
    path: `${CONNECTORS_PATH}/plugins`,
    additionalHeaders: JSON_HEADERS,
    signal,
  })
  const body = (await response.json()) as PluginsResponse
  return body.plugins ?? []
}

/** Starts a connection. Returns `consent_link` when the provider needs authorization. */
export async function connectConnector(connector: string, signal?: AbortSignal): Promise<ConnectorConnectionResponse> {
  const response = await fetchCAPI({
    path: connectionPath(connector),
    method: 'PUT',
    body: {client_source: CLIENT_SOURCE},
    additionalHeaders: JSON_HEADERS,
    signal,
  })
  return (await response.json()) as ConnectorConnectionResponse
}

/** Removes the connection. The proxy answers 204 with no body. */
export async function disconnectConnector(connector: string, signal?: AbortSignal): Promise<void> {
  await fetchCAPI({
    path: connectionPath(connector),
    method: 'DELETE',
    additionalHeaders: JSON_HEADERS,
    signal,
  })
}
